import React, { useEffect, useState } from 'react';
import { ValidationFields } from './ValidationFields';
import { DocumentViewer } from './DocumentViewer';
import { AlertCircleIcon, CheckCircleIcon, FileTextIcon, AlertTriangleIcon } from 'lucide-react';
// Sample document queue data
const sampleDocumentQueue = [{
  id: 'doc-001',
  type: 'Invoice',
  vendor: 'Office Depot',
  date: '2023-10-15',
  total: 1250.75,
  status: 'Pending Validation',
  confidence: 0.85
}, {
  id: 'doc-002',
  type: 'Credit Note',
  vendor: 'Dell Technologies',
  date: '2023-10-12',
  total: 430.0,
  status: 'Pending Validation',
  confidence: 0.72
}, {
  id: 'doc-003',
  type: 'Invoice',
  vendor: 'PrintWorks Inc.',
  date: '2023-10-05',
  total: 3200.5,
  status: 'Exception',
  confidence: 0.45
}];
// Sample DI extracted data
const sampleExtractedData = {
  documentType: 'Invoice',
  confidence: {
    documentType: 0.95,
    invoiceNumber: 0.88,
    invoiceDate: 0.92,
    dueDate: 0.75,
    vendorName: 0.82,
    vendorAddress: 0.65,
    total: 0.9,
    subtotal: 0.85,
    tax: 0.78,
    currency: 0.95
  },
  header: {
    invoiceNumber: 'INV-2023-4501',
    invoiceDate: '2023-10-15',
    dueDate: '2023-11-15',
    poReference: 'PO-2023-001',
    currency: 'USD',
    total: 1250.75,
    subtotal: 1150.75,
    tax: 100.0
  },
  vendor: {
    name: 'Office Depot',
    address: '123 Business Ave, Suite 100, San Francisco, CA 94107',
    taxId: 'US-123456789',
    vendorId: 'VEN-001',
    email: 'invoices@officedepot.com',
    phone: '(555) 123-4567'
  },
  lineItems: [{
    id: 1,
    description: 'Premium Paper 8.5x11 (Case)',
    quantity: 5,
    unitPrice: 45.99,
    total: 229.95,
    tax: 20.0,
    confidence: 0.88
  }, {
    id: 2,
    description: 'Ballpoint Pens (Box of 12)',
    quantity: 10,
    unitPrice: 8.99,
    total: 89.9,
    tax: 8.0,
    confidence: 0.92
  }, {
    id: 3,
    description: 'Desk Organizer - Black',
    quantity: 15,
    unitPrice: 24.99,
    total: 374.85,
    tax: 32.0,
    confidence: 0.75
  }, {
    id: 4,
    description: 'Wireless Mouse',
    quantity: 8,
    unitPrice: 19.99,
    total: 159.92,
    tax: 14.0,
    confidence: 0.85
  }, {
    id: 5,
    description: 'Desk Lamp - LED',
    quantity: 12,
    unitPrice: 24.99,
    total: 299.88,
    tax: 26.0,
    confidence: 0.8
  }],
  glCoding: {
    department: '100',
    account: '5001',
    project: 'PROJ-001',
    costCenter: 'CC-123'
  },
  auditTrail: [{
    field: 'invoiceNumber',
    originalValue: 'INV-2023-4591',
    newValue: 'INV-2023-4501',
    changedBy: 'DI System',
    changedAt: '2023-10-16T09:30:00Z',
    confidence: 0.88
  }, {
    field: 'vendorAddress',
    originalValue: '123 Busines Ave, Suite 100, San Francisco, CA 94107',
    newValue: '123 Business Ave, Suite 100, San Francisco, CA 94107',
    changedBy: 'DI System',
    changedAt: '2023-10-16T09:30:00Z',
    confidence: 0.65
  }]
};
// Sample document image URL
const sampleDocumentUrl = 'https://templates.invoicehome.com/invoice-template-us-neat-750px.png';
export function DIValidation({
  locale = 'en-US'
}) {
  const [currentDocument, setCurrentDocument] = useState(sampleDocumentQueue[0]);
  const [extractedData, setExtractedData] = useState(sampleExtractedData);
  const [documentImage, setDocumentImage] = useState(sampleDocumentUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  // Simulate loading a document from the queue
  const loadDocument = document => {
    setIsLoading(true);
    setCurrentDocument(document);
    // Simulate API call to fetch document data
    setTimeout(() => {
      setExtractedData(sampleExtractedData);
      setDocumentImage(sampleDocumentUrl);
      setIsLoading(false);
    }, 800);
  };
  // Handle field updates
  const handleFieldUpdate = (section, field, value) => {
    setExtractedData(prevData => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value
      },
      auditTrail: [{
        field: `${section}.${field}`,
        originalValue: prevData[section][field],
        newValue: value,
        changedBy: 'Current User',
        changedAt: new Date().toISOString(),
        confidence: 1.0 // Manual edits have 100% confidence
      }, ...prevData.auditTrail]
    }));
  };
  // Handle line item updates
  const handleLineItemUpdate = (index, field, value) => {
    setExtractedData(prevData => {
      const updatedLineItems = [...prevData.lineItems];
      updatedLineItems[index] = {
        ...updatedLineItems[index],
        [field]: value
      };
      return {
        ...prevData,
        lineItems: updatedLineItems,
        auditTrail: [{
          field: `lineItems[${index}].${field}`,
          originalValue: prevData.lineItems[index][field],
          newValue: value,
          changedBy: 'Current User',
          changedAt: new Date().toISOString(),
          confidence: 1.0
        }, ...prevData.auditTrail]
      };
    });
  };
  // Handle document validation submission
  const handleValidate = status => {
    setIsLoading(true);
    // Simulate API call to submit validation
    setTimeout(() => {
      setValidationStatus(status);
      setIsLoading(false);
    }, 800);
  };
  // Reset validation status when changing documents
  useEffect(() => {
    setValidationStatus(null);
  }, [currentDocument]);
  return <div className="mb-8" data-id="element-4461">
      <div className="flex justify-between items-center mb-6" data-id="element-4462">
        <div data-id="element-4463">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-4464">DI Validation</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4465">
            Review and validate data extracted from scanned documents
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-4466">
          <button onClick={() => handleValidate('approved')} disabled={isLoading || validationStatus} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${isLoading || validationStatus ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'}`} data-id="element-4467">
            <CheckCircleIcon className="h-4 w-4 mr-2" data-id="element-4468" />
            Validate
          </button>
          <button onClick={() => handleValidate('exception')} disabled={isLoading || validationStatus} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${isLoading || validationStatus ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'}`} data-id="element-4469">
            <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-4470" />
            Flag Exception
          </button>
        </div>
      </div>
      {validationStatus && <div className={`mb-4 p-4 rounded-md ${validationStatus === 'approved' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-id="element-4471">
          <div className="flex" data-id="element-4472">
            <div className="flex-shrink-0" data-id="element-4473">
              {validationStatus === 'approved' ? <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" data-id="element-4474" /> : <AlertCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" data-id="element-4475" />}
            </div>
            <div className="ml-3" data-id="element-4476">
              <h3 className={`text-sm font-medium ${validationStatus === 'approved' ? 'text-green-800' : 'text-red-800'}`} data-id="element-4477">
                {validationStatus === 'approved' ? 'Document validated successfully' : 'Document flagged for exception review'}
              </h3>
              <div className={`mt-2 text-sm ${validationStatus === 'approved' ? 'text-green-700' : 'text-red-700'}`} data-id="element-4478">
                <p data-id="element-4479">
                  {validationStatus === 'approved' ? 'The document has been validated and will now proceed to the next stage in the workflow.' : 'The document has been sent to the exceptions queue for further review.'}
                </p>
              </div>
            </div>
          </div>
        </div>}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4480">
        {/* Document Queue */}
        <div className="lg:col-span-1" data-id="element-4481">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4482">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-4483">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4484">
                Document Queue
              </h3>
            </div>
            <ul className="divide-y divide-gray-200" data-id="element-4485">
              {sampleDocumentQueue.map(document => <li key={document.id} className={`px-4 py-4 hover:bg-gray-50 cursor-pointer ${currentDocument.id === document.id ? 'bg-indigo-50' : ''}`} onClick={() => loadDocument(document)} data-id="element-4486">
                  <div className="flex items-center justify-between" data-id="element-4487">
                    <div className="flex items-center" data-id="element-4488">
                      {document.type === 'Invoice' ? <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-4489" /> : <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-4490" />}
                      <span className="text-sm font-medium text-gray-900" data-id="element-4491">
                        {document.vendor}
                      </span>
                    </div>
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${document.confidence < 0.6 ? 'bg-red-100 text-red-800' : document.confidence < 0.8 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-4492">
                      {Math.round(document.confidence * 100)}%
                    </div>
                  </div>
                  <div className="mt-2 flex justify-between" data-id="element-4493">
                    <div className="flex items-center text-sm text-gray-500" data-id="element-4494">
                      <span data-id="element-4495">{document.type}</span>
                      <span className="mx-1" data-id="element-4496">•</span>
                      <span data-id="element-4497">${document.total.toFixed(2)}</span>
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4498">{document.date}</div>
                  </div>
                  {document.status === 'Exception' && <div className="mt-2 flex items-center text-xs text-red-600" data-id="element-4499">
                      <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-4500" />
                      Exception
                    </div>}
                </li>)}
            </ul>
          </div>
        </div>
        {/* Main Content */}
        <div className="lg:col-span-3" data-id="element-4501">
          {isLoading ? <div className="flex justify-center items-center h-96 bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4502">
              <div className="text-center" data-id="element-4503">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto" data-id="element-4504"></div>
                <p className="mt-4 text-gray-600" data-id="element-4505">Loading document...</p>
              </div>
            </div> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4506">
              {/* Validation Fields (Left Panel) */}
              <ValidationFields extractedData={extractedData} onFieldUpdate={handleFieldUpdate} onLineItemUpdate={handleLineItemUpdate} locale={locale} data-id="element-4507" />
              {/* Document Viewer (Right Panel) */}
              <DocumentViewer documentUrl={documentImage} extractedData={extractedData} data-id="element-4508" />
            </div>}
        </div>
      </div>
    </div>;
}